/**
 * useExtCompany.jsx — 회사별(ext) 페이지의 회사 확인 (2026-09-10 구조 개편 2차)
 *
 * ExternalCompany*.jsx 5개가 **같은 코드를 복붙**하고 있었다 — URL 의 :code 로
 * /api/my/external-companies 를 뒤져 회사를 찾고, 기능이 켜져 있는지 보고, 로딩·에러를 그린다.
 * 다른 것은 검사하는 기능 키와 문구뿐이었다. 그 한 벌을 여기로 모은다.
 *
 * ⚠ 선택창(CompanyScopeContext)의 선택 회사를 쓰지 않고 **URL 의 :code 를 그대로** 쓴다.
 *   선택창 값은 딥링크 동기화가 비동기라 URL 과 잠깐 어긋날 수 있고, 그 사이에 화면이
 *   **다른 회사 데이터를 그릴** 수 있다. 회사 경계에서 그런 틈은 만들지 않는다.
 *
 * ⚠ 기능 검사 방식이 화면마다 원래 달랐다. 그대로 보존한다 —
 *   truthy   : 값이 참일 때만 통과 (출퇴근·연차. undefined 도 차단)
 *   not-false: 명시적 false 일 때만 차단 (일일콜. undefined 는 통과)
 *   검사 없음 : 직원 관리
 */
import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/apiClient';

/**
 * @param {string} code            URL 의 회사 코드
 * @param {{key:string,label:string,mode:'truthy'|'not-false'}} [check] 기능 검사(생략 가능)
 */
export function useExtCompany(code, check) {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const checkKey = check?.key || '';
  const checkLabel = check?.label || '';
  const checkMode = check?.mode || '';

  useEffect(() => {
    if (!code) return undefined;
    let active = true;
    setLoading(true);
    setError(null);

    // ?scope=selected: 운영자가 작업대상으로 고른 고객사 회사도 목록에 넣어 달라는 표시(2026-09-24).
    //   서버는 운영자일 때만 따른다 — 다른 사람에겐 붙이지 않은 것과 같은 목록이 온다.
    apiClient.get('/api/my/external-companies?scope=selected')
      .then((res) => {
        if (!active) return;
        const found = (res.data?.list || []).find(
          (c) => String(c.code).toUpperCase() === String(code).toUpperCase(),
        );
        if (!found) {
          setError(`'${code}' 회사를 찾을 수 없거나 접근 권한이 없습니다.`);
          return;
        }
        const value = checkKey ? found.enabled_features?.[checkKey] : undefined;
        const blocked = checkMode === 'truthy' ? !value : (checkMode === 'not-false' && value === false);
        if (blocked) {
          setError(`'${found.name}' 회사는 ${checkLabel} 기능이 비활성화되어 있습니다.`);
          return;
        }
        setCompany(found);
      })
      .catch(() => { if (active) setError('회사 정보를 불러오는 데 실패했습니다.'); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [code, checkKey, checkLabel, checkMode]);

  return { company, loading, error };
}

/** 로딩·에러 화면 (다섯 곳이 똑같이 그리던 것) */
export function ExtCompanyFallback({ loading, error }) {
  if (loading) {
    return (
      <div style={{ padding: 32, color: 'var(--text-muted)', textAlign: 'center' }}>
        로딩 중...
      </div>
    );
  }
  return (
    <div style={{ padding: 32, color: '#ef4444', textAlign: 'center' }}>
      {error}
    </div>
  );
}
